import { Component, Inject, inject, PLATFORM_ID, ViewChild, AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { OrganizationInfoFormComponent } from "../organization-info-form/organization-info-form.component";
import { MatCardModule } from '@angular/material/card';
import { FormStateService, PlatformPackageId } from '../../services/form-state.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { BrandingInfoFormComponent } from '../branding-info-form/branding-info-form.component';
import { PaymentInfoFormComponent } from '../payment-info-form/payment-info-form.component';
import { PackageSelectFormComponent } from '../package-select-form/package-select-form.component';
import { ClientOnboardingService, ClientRegistrationResponse, ProvisioningStatus } from '../../services/client-onboarding.service';
import { TurnstileWidgetComponent } from '../turnstile-widget/turnstile-widget.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '@env/environment';
import { AnalyticsService } from '../../../shared/services/analytics.service';
import { Subscription, interval } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';

type SubmitAction = 'skipBranding' | 'skipPayment' | 'createAccount';
type SetupStepState = 'complete' | 'current' | 'pending' | 'error';

interface SetupStepViewModel {
  title: string;
  description: string;
  state: SetupStepState;
}


@Component({
  selector: 'app-onboarding-page',
  imports: [
    CommonModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    PackageSelectFormComponent,
    OrganizationInfoFormComponent,
    BrandingInfoFormComponent,
    PaymentInfoFormComponent,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    TurnstileWidgetComponent,
],
  templateUrl: './onboarding-page.component.html',
  styleUrl: './onboarding-page.component.scss'
})
export class OnboardingPageComponent implements OnInit, AfterViewInit, OnDestroy {
    isLoading = false;
    showSuccessPage = false;
    registrationResponse: ClientRegistrationResponse | null = null;
    signUpForm!: FormGroup;
    organizationInformationForm!: FormGroup;
    adminInformationForm!: FormGroup;
    packageInformationForm!: FormGroup;
    paymentMethodId: string | null = null;
    @ViewChild('stepper') stepper!: MatStepper;
    @ViewChild(PaymentInfoFormComponent) paymentForm?: PaymentInfoFormComponent;
    private readonly _snackBar = inject(MatSnackBar);
    private readonly analytics = inject(AnalyticsService);
    
    // Provisioning status polling
    provisioningStatus: ProvisioningStatus | null = null;
    isProvisioningFailed = false;
    activeSubmitAction: SubmitAction | null = null;
    setupSteps: SetupStepViewModel[] = [];
    private pollingSubscription?: Subscription;

    // Email verification + bot check (must pass before anything is provisioned server-side)
    @ViewChild(TurnstileWidgetComponent) turnstileWidget?: TurnstileWidgetComponent;
    turnstileToken: string | null = null;
    verificationId: string | null = null;
    verificationEmail: string | null = null;
    emailVerified = false;
    isSendingCode = false;
    isCheckingCode = false;
    verificationError: string | null = null;
    readonly verificationCodeControl = new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^\s*\d{6}\s*$/)]
    });
    private emailChangesSubscription?: Subscription;
    
    constructor(
      private readonly formStateService: FormStateService,
      private readonly clientOnboardingService: ClientOnboardingService,
      private readonly router: Router,
      private readonly route: ActivatedRoute,
      @Inject(PLATFORM_ID) private readonly platformId: Object
    ) {
      this.signUpForm = this.formStateService.getForm();
      this.packageInformationForm = this.signUpForm.get('packageInformation') as FormGroup;
      this.organizationInformationForm = this.signUpForm.get('organizationInformation') as FormGroup;
      this.adminInformationForm = this.signUpForm.get('adminInformation') as FormGroup;
    }

    ngOnInit(): void {
      // A changed admin email needs a fresh code.
      this.emailChangesSubscription = this.adminInformationForm.get('email')?.valueChanges.subscribe((value: string) => {
        if (this.verificationEmail && this.normalizeEmail(value) !== this.verificationEmail) {
          this.resetEmailVerification();
        }
      });

      const plan = this.route.snapshot.queryParamMap.get('plan');
      if (plan === 'community') {
        this.formStateService.setPlatformPackage('society');
      } else if (plan === 'society' || plan === 'growth') {
        this.formStateService.setPlatformPackage(plan);
      }
    }
    
    ngOnDestroy(): void {
      this.stopPolling();
      this.emailChangesSubscription?.unsubscribe();
    }

    ngAfterViewInit() {
      // Scroll to top when step changes
      if (this.stepper) {
        this.stepper.selectionChange.subscribe(() => {
          if (isPlatformBrowser(this.platformId)) {
            // Smooth scroll to top of the page
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        });
      }
    }

    get selectedPackage(): PlatformPackageId {
      return this.formStateService.getPlatformPackage();
    }

    get requiresPaymentMethod(): boolean {
      return this.formStateService.requiresPaymentMethod();
    }

    isPackageStepValid(): boolean {
      return this.packageInformationForm.valid;
    }

    isStep1Valid(): boolean {
      return this.organizationInformationForm.valid && this.adminInformationForm.valid;
    }

    getBillingEmail(): string {
      return this.adminInformationForm.get('email')?.value || '';
    }

    onPaymentMethodCreated(paymentMethodId: string): void {
      this.paymentMethodId = paymentMethodId;
      this.submit('createAccount');
    }

    onPaymentSkipped(): void {
      if (this.requiresPaymentMethod) {
        this._snackBar.open('Society and Growth require a payment method to start.', 'OK', { duration: 4000 });
        return;
      }
      this.paymentMethodId = null;
      this.submit('skipPayment');
    }

    async createAccountFromPaymentStep(): Promise<void> {
      if (this.requiresPaymentMethod || this.paymentForm?.canSubmit()) {
        await this.paymentForm?.confirmPaymentMethod();
        return;
      }

      this.submit('createAccount');
    }

    continuePastAccountSetup(): void {
      if (!this.isStep1Valid()) {
        this._snackBar.open('Please fill in all required fields.', 'OK', { duration: 3000 });
        return;
      }

      if (this.emailVerified) {
        this.stepper.next();
        return;
      }

      if (!this.verificationId) {
        this.sendVerificationCode();
        return;
      }

      this.confirmVerificationCode();
    }

    get accountStepActionLabel(): string {
      if (this.emailVerified) {
        return 'Continue to Branding';
      }
      if (this.verificationId) {
        return this.isCheckingCode ? 'Checking code...' : 'Confirm Code';
      }
      return this.isSendingCode ? 'Sending code...' : 'Verify Email';
    }

    get canContinuePastAccountSetup(): boolean {
      if (this.isLoading || this.isSendingCode || this.isCheckingCode || !this.isStep1Valid()) {
        return false;
      }
      if (this.emailVerified) {
        return true;
      }
      return this.verificationId ? this.verificationCodeControl.valid : !!this.turnstileToken;
    }

    onTurnstileToken(token: string | null): void {
      this.turnstileToken = token;
    }

    /** Bot check + email a 6-digit code. The API creates nothing until the code is confirmed at registration. */
    sendVerificationCode(): void {
      if (!this.turnstileToken) {
        this._snackBar.open('Please complete the security check first.', 'OK', { duration: 4000 });
        return;
      }

      const org = this.organizationInformationForm.value;
      const admin = this.adminInformationForm.value;
      this.isSendingCode = true;
      this.verificationError = null;
      this.clientOnboardingService.startEmailVerification({
        email: admin.email,
        organizationName: org.name,
        firstName: admin.firstName,
        lastName: admin.lastName,
        turnstileToken: this.turnstileToken
      }).subscribe({
        next: (resp) => {
          this.isSendingCode = false;
          this.verificationId = resp.verificationId;
          this.verificationEmail = this.normalizeEmail(resp.email || admin.email);
          this.verificationCodeControl.reset('');
          this.turnstileToken = null;
        },
        error: (err) => {
          this.isSendingCode = false;
          // Turnstile tokens are single-use; get a fresh one for the retry.
          this.turnstileToken = null;
          this.turnstileWidget?.reset();
          this.showErrorMessage(err);
        }
      });
    }

    confirmVerificationCode(): void {
      if (!this.verificationId || this.verificationCodeControl.invalid) {
        this.verificationError = 'Enter the 6-digit code from your email.';
        return;
      }

      this.isCheckingCode = true;
      this.verificationError = null;
      this.clientOnboardingService.verifyEmailCode(this.adminInformationForm.value.email, {
        verificationId: this.verificationId,
        code: this.verificationCodeControl.value.trim()
      }).subscribe({
        next: () => {
          this.isCheckingCode = false;
          this.emailVerified = true;
          this.stepper.next();
        },
        error: (err) => {
          this.isCheckingCode = false;
          this.verificationError = this.errorText(err);
          if (this.needsNewCode(err)) {
            const message = this.verificationError;
            this.resetEmailVerification();
            this.verificationError = message;
          }
        }
      });
    }

    /** Lets the admin fix the email or get a new code (requires a fresh security check). */
    requestNewCode(): void {
      this.resetEmailVerification();
    }

    resetEmailVerification(): void {
      this.verificationId = null;
      this.verificationEmail = null;
      this.emailVerified = false;
      this.turnstileToken = null;
      this.verificationError = null;
      this.verificationCodeControl.reset('');
    }

    private needsNewCode(err: any): boolean {
      const code: string = err?.error?.code || '';
      return code === 'email_verification_expired'
        || code === 'email_verification_toomanyattempts'
        || code === 'email_verification_alreadyused';
    }

    private normalizeEmail(value: string | null | undefined): string {
      return (value || '').trim().toLowerCase();
    }

    skipBrandingAndContinue(): void {
      // Always continue to payment so Society/Growth can collect a card.
      this.stepper.next();
    }

    submit(action: SubmitAction = 'createAccount') {
      if (isPlatformBrowser(this.platformId)) {
        // Validate required forms before submission
        if (!this.isPackageStepValid() || !this.isStep1Valid()) {
          this._snackBar.open('Please fill in all required fields.', 'OK', { duration: 3000 });
          return;
        }

        if (this.requiresPaymentMethod && !this.paymentMethodId) {
          this._snackBar.open('Society and Growth require a payment method to start.', 'OK', { duration: 4000 });
          return;
        }

        if (!this.emailVerified || !this.verificationId) {
          this._snackBar.open('Please verify your email before creating the account.', 'OK', { duration: 4000 });
          if (this.stepper) {
            this.stepper.selectedIndex = 1;
          }
          return;
        }

        this.activeSubmitAction = action;
        this.isLoading = true;
        this.clientOnboardingService.registerClient(this.signUpForm, this.paymentMethodId, {
          verificationId: this.verificationId,
          code: this.verificationCodeControl.value.trim()
        }).subscribe({
          next: (resp) => {
            this.isLoading = false;
            this.activeSubmitAction = null;
            this.registrationResponse = resp;
            this.analytics.trackSignUp('register_organization', this.selectedPackage);
            this.provisioningStatus = {
              clientId: resp.client.id,
              status: resp.provisioningStatus,
              portalReady: false,
              currentStepTitle: 'Starting portal setup',
              currentStepDescription: resp.provisioningMessage || 'Your organization has been created. We are starting the remaining setup now.'
            };
             
            // Show success page - portal is being provisioned in background
            this.showSuccessPage = true;
            this.updateSetupProgress();
             
            // Scroll to top to show success message
            globalThis.scrollTo({ top: 0, behavior: 'smooth' });
            
            // Start polling for provisioning status
            this.startPolling(resp.client.id);
          },
          error: (err) => {
            console.error('Registration failed:', err);
            this.isLoading = false;
            this.activeSubmitAction = null;
            this.showErrorMessage(err);
            if (String(err?.error?.code || '').startsWith('email_verification_')) {
              // The code was rejected server-side: send the admin back to get a new one.
              this.resetEmailVerification();
              if (this.stepper) {
                this.stepper.selectedIndex = 1;
              }
            }
          }
        });
      }
    }
    
    private startPolling(clientId: number): void {
      // Poll until portal is actually ready (CloudFront/S3 saved) or failed. Don't stop on status 'completed' until portalReady is true.
      this.pollingSubscription = interval(3000).pipe(
        switchMap(() => this.clientOnboardingService.getProvisioningStatus(clientId)),
        takeWhile(
          status => status.status === 'pending' || status.status === 'in_progress' || (status.status === 'completed' && !status.portalReady),
          true
        )
      ).subscribe({
        next: (status) => {
          this.provisioningStatus = status;
          this.isProvisioningFailed = status.status === 'failed';
          this.updateSetupProgress();

          if (status.portalReady) {
            this.stopPolling();
          } else if (status.status === 'failed') {
            this.stopPolling();
          }
        },
        error: (err) => {
          console.error('Error polling provisioning status:', err);
        }
      });
    }
    
    private stopPolling(): void {
      if (this.pollingSubscription) {
        this.pollingSubscription.unsubscribe();
        this.pollingSubscription = undefined;
      }
    }
    
    goToPortal(): void {
      const portalUrl = this.provisioningStatus?.portalUrl || this.getPortalUrl();
      if (portalUrl) {
        globalThis.open(portalUrl, '_blank');
      }
    }

    getPortalUrl(): string {
      const domain = this.registrationResponse?.client.domain || 
        this.signUpForm.get('organizationInformation')?.get('domain')?.value;
      
      if (environment.environment === 'local') {
        return 'https://localhost:4200';
      }
      
      const baseDomain = environment.environment === 'dev' ? 'dev.membertrace.com' : 'membertrace.com';
      return `https://${domain}.${baseDomain}`;
    }

    getAdminEmail(): string {
      return this.registrationResponse?.member ?
        this.adminInformationForm.get('email')?.value :
        this.adminInformationForm.get('email')?.value;
    }

    /** True when CloudFront/S3 are saved and the portal is ready; show "Your portal is ready" and "Welcome email sent" only then. */
    get isPortalReady(): boolean {
      return this.provisioningStatus?.status === 'completed' && this.provisioningStatus?.portalReady === true;
    }

    get setupStatusTitle(): string {
      if (this.isPortalReady) {
        return 'Portal ready';
      }

      if (this.isProvisioningFailed) {
        return this.provisioningStatus?.currentStepTitle || 'Setup needs attention';
      }

      return this.provisioningStatus?.currentStepTitle || 'Starting portal setup';
    }

    get setupStatusDescription(): string {
      if (this.isPortalReady) {
        return 'Your portal is ready to open. Verify your email, then sign in.';
      }

      if (this.isProvisioningFailed) {
        return this.provisioningStatus?.error || this.provisioningStatus?.currentStepDescription || 'We hit an issue while finishing setup.';
      }

      return this.provisioningStatus?.currentStepDescription || 'We are finishing the remaining setup steps now.';
    }

    getSetupStepIcon(state: SetupStepState): string {
      switch (state) {
        case 'complete':
          return 'check_circle';
        case 'current':
          return 'autorenew';
        case 'error':
          return 'error';
        default:
          return 'radio_button_unchecked';
      }
    }

    private updateSetupProgress(): void {
      const status = this.provisioningStatus;
      const storageReady = status?.storageReady === true;
      const activationReady = status?.activationReady === true;
      const welcomeEmailSent = status?.welcomeEmailSent === true;
      const failed = status?.status === 'failed';
      const adminEmail = this.getAdminEmail();

      this.setupSteps = [
        {
          title: 'Account created',
          description: 'Your organization and admin sign-in were saved successfully.',
          state: 'complete'
        },
        {
          title: 'Portal storage',
          description: 'Provisioning your media storage and portal domain.',
          state: failed && !storageReady ? 'error' : storageReady ? 'complete' : 'current'
        },
        {
          title: 'Portal access',
          description: 'Activating your portal and finishing connected billing setup.',
          state: failed && storageReady && !activationReady ? 'error' : activationReady ? 'complete' : storageReady ? 'current' : 'pending'
        },
        {
          title: 'Welcome email',
          description: `Sending portal details to ${adminEmail}.`,
          state: failed && activationReady && !welcomeEmailSent ? 'error' : welcomeEmailSent ? 'complete' : activationReady ? 'current' : 'pending'
        }
      ];
    }

    showErrorMessage(error: any) {
      this._snackBar.open(`Error: ${this.errorText(error)}`, '', {duration: 5000});
    }

    private errorText(error: any): string {
      return error?.error?.message || error?.error?.error || error?.message || 'Something went wrong. Please try again.';
    }
    
}
